
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';
import PricingRefreshButton from './PricingRefreshButton';
import PricingAuditLog from './PricingAuditLog';
import { formatCurrency } from '@/lib/utils';

interface PricingSummaryCardProps {
  baseCost: number;
  effectiveMarkup: number;
  effectiveMargin: number;
  finalPrice: number;
  overrideMarkup: boolean;
  onOverrideToggle: (checked: boolean) => void;
  markupResult: {
    sourceLabel: string;
    sourceName: string;
  };
  lineItemId?: string;
  categoryId?: string;
  companyId?: string;
  customTiers: LineItemMarkupTier[];
  useCustomTiers: boolean;
  onPricingRefreshed?: () => void;
}

const PricingSummaryCard: React.FC<PricingSummaryCardProps> = ({
  baseCost,
  effectiveMarkup,
  effectiveMargin,
  finalPrice,
  overrideMarkup,
  onOverrideToggle,
  markupResult,
  lineItemId,
  categoryId,
  companyId,
  customTiers,
  useCustomTiers,
  onPricingRefreshed
}) => {
  const getPricingSourceLabel = () => {
    if (overrideMarkup) return "Custom (Override)";
    return markupResult.sourceLabel;
  };
  
  const getPricingBadgeVariant = () => {
    if (overrideMarkup) return "default";
    
    switch(markupResult.sourceName) {
      case 'custom': return "secondary";
      case 'category': return "outline";
      case 'global': return "default";
      default: return "default";
    }
  };
  
  const getPricingBadgeColorClass = () => {
    if (overrideMarkup) return "bg-blue-500";
    
    switch(markupResult.sourceName) {
      case 'custom': return "bg-purple-500";
      case 'category': return "bg-green-500";
      case 'global': return "bg-orange-500";
      default: return "";
    }
  };

  return (
    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Pricing Summary</h3>
        <div className="flex items-center space-x-2">
          {lineItemId && (
            <>
              <PricingRefreshButton
                lineItemId={lineItemId}
                currentBaseCost={baseCost}
                categoryId={categoryId}
                companyId={companyId}
                customTiers={customTiers}
                useCustomTiers={useCustomTiers}
                onPricingRefreshed={onPricingRefreshed}
              />
              <PricingAuditLog lineItemId={lineItemId} />
            </>
          )}
          <span className="text-sm text-muted-foreground">Override Calculated Pricing</span>
          <Switch 
            checked={overrideMarkup}
            onCheckedChange={onOverrideToggle}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Base Price:</span>
          <span className="font-semibold">{formatCurrency(baseCost)}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Pricing Source:</span>
          <Badge 
            variant={getPricingBadgeVariant()}
            className={getPricingBadgeColorClass()}
          >
            {getPricingSourceLabel()}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Markup:</span>
          <span className="font-semibold">{effectiveMarkup.toFixed(2)}%</span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Margin:</span>
          <span className="font-semibold">{effectiveMargin.toFixed(2)}%</span>
        </div>
        
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Final Price:</span>
            <span className="font-semibold text-green-600">{formatCurrency(finalPrice)}</span>
            <span className="text-xs text-muted-foreground">(Calculated Live)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSummaryCard;
